import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { ConfirmDialogModel, ConfirmDialogService, UiButtonComponent, UiToggleComponent } from '@ui';
import { UiPreloaderComponent } from '@ui';
import { PreloaderColor } from '@enums/shared.enum';
import { SupportAccessModel } from '@models/support-access.model';
import { LocationActions } from '@states/location/location.action-types';
import { SupportAccessActions } from '@states/support-access/support-access.action-types';
import { SupportAccessSelectors } from '@states/support-access/support-access.selector-types';
import { EnableEscCloseDirective } from '../../shared/directives/enable-esc-close.directive';

const LIFETIME_DURATION_DAYS = 100 * 365;
const DURATION_OPTIONS = [1, 3, 30, 60, 90, LIFETIME_DURATION_DAYS];
const DEFAULT_DURATION_DAYS = 30;

@Component({
  selector: 'app-support-access-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    UiPreloaderComponent,
    FormsModule,
    ReactiveFormsModule,
    UiToggleComponent,
    MatSelectModule,
    UiButtonComponent,
  ],
  templateUrl: './support-access-modal.component.html',
  styleUrl: './support-access-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class SupportAccessModalComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<SupportAccessModalComponent>);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  public readonly supportAccessList = this.store$.selectSignal(SupportAccessSelectors.selectSupportAccessDocuments);
  public readonly isLoading = this.store$.selectSignal(SupportAccessSelectors.selectIsLoading);
  public readonly loaderColor = PreloaderColor;
  public readonly durationOptions = DURATION_OPTIONS;
  public readonly lifetimeDurationDays = LIFETIME_DURATION_DAYS;

  public readonly currentDocument = computed<SupportAccessModel.Document | null>(
    () => this.supportAccessList()[0] ?? null,
  );

  public readonly isDocumentActive = computed<boolean>(() => {
    const doc = this.currentDocument();
    return !!doc && doc.expiredAt > Date.now();
  });

  public readonly toggleControl = new FormControl<boolean>(false, { nonNullable: true });
  private readonly toggleRef = viewChild.required<UiToggleComponent>('toggleRef');

  public readonly selectedDurationDays = signal<number>(DEFAULT_DURATION_DAYS);

  private initialized = false;

  constructor() {
    effect(() => {
      const active = this.isDocumentActive();
      const doc = this.currentDocument();
      if (!this.initialized && (doc !== null || !this.isLoading())) {
        this.initialized = true;
        this.toggleControl.setValue(active, { emitEvent: false });
        this.selectedDurationDays.set(doc?.durationDays ?? DEFAULT_DURATION_DAYS);
      }
    });
  }

  public ngOnInit(): void {
    this.store$.dispatch(LocationActions.GetLocations());
    this.store$.dispatch(SupportAccessActions.getSupportAccess());
  }

  public onDurationChange(durationDays: number): void {
    this.selectedDurationDays.set(durationDays);
  }

  public onSave(): void {
    const wasActive = this.isDocumentActive();
    const nowEnabled = this.toggleControl.value;
    const doc = this.currentDocument();
    const duration = this.selectedDurationDays();

    if (!wasActive && nowEnabled) {
      this.store$.dispatch(SupportAccessActions.createSupportAccess({
        body: { ttl: duration * 24 * 60 * 60 },
      }));
      this.dialogRef.close();
      return;
    }

    if (wasActive && !nowEnabled) {
      this.confirmDialogService
        .open({
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: 'ACCOUNT.SUPPORT_ACCESS.DISABLE_CONFIRM.TITLE',
          msg: 'ACCOUNT.SUPPORT_ACCESS.DISABLE_CONFIRM.MESSAGE',
          confirm: 'COMMON.DISABLE',
          cancel: 'COMMON.CANCEL',
          disableClose: true,
        })
        .pipe(take(1))
        .subscribe((result) => {
          if (result?.selection !== ConfirmDialogModel.ConfirmResult.OK) {
            this.toggleControl.setValue(true, { emitEvent: false });
            this.cdr.markForCheck();
            return;
          }
          this.store$.dispatch(SupportAccessActions.deleteSupportAccess({ id: doc!._id }));
          this.dialogRef.close();
        });
      return;
    }

    if (wasActive && nowEnabled && doc!.durationDays !== duration) {
      this.store$.dispatch(SupportAccessActions.updateSupportAccess({
        id: doc!._id,
        body: { ttl: duration * 24 * 60 * 60 },
      }));
    }

    this.dialogRef.close();
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
